import Image from 'next/image';
import { usePlausible } from 'next-plausible';
import { useEffect, useState } from 'react';
import { getRoles } from '../../../helpers/token';
import { figtree, fraunces, readingSerif } from '../../Landing/fonts';
import appStoreBadge from '../../../images/app-store.svg';
import playStoreBadge from '../../../images/play-store.svg';

const APP_STORE_URL =
  'https://apps.apple.com/app/apple-store/id6670431055?pt=122664205&ct=travelfeed-post&mt=8';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.curlycorn.menutranslator&referrer=utm_source%3Dtravelfeed%26utm_medium%3Dpost';
const WEBSITE_URL =
  'https://www.menutranslatorapp.com/?utm_source=travelfeed&utm_medium=post';

// One little menu per popular food destination — each highlighting a dish
// that literal translators butcher ("ropa vieja" = old clothes). The card
// shows the menu matching the country the story is about.
const MENUS = {
  default: {
    dishes: [
      { name: 'Polvo à Lagareiro', price: '14€' },
      { name: 'Cataplana de Marisco', price: '18€' },
      { name: 'Arroz de Pato', price: '12€' },
    ],
    highlight: 0,
    explain: 'Octopus roasted in olive oil & garlic',
    tags: '🐙 Shellfish · 🌾 Gluten-free · “POL-voo”',
  },
  es: {
    dishes: [
      { name: 'Ropa Vieja', price: '12€' },
      { name: 'Pulpo a la Gallega', price: '15€' },
      { name: 'Rabo de Toro', price: '16€' },
    ],
    highlight: 0,
    explain: 'Shredded-beef stew — not “old clothes”',
    tags: '🥘 Beef · 🌶 Mild · “ROH-pah VYEH-hah”',
  },
  it: {
    dishes: [
      { name: 'Strozzapreti al Ragù', price: '11€' },
      { name: 'Saltimbocca', price: '14€' },
      { name: 'Coda alla Vaccinara', price: '13€' },
    ],
    highlight: 0,
    explain: 'Hand-rolled pasta — literally “priest stranglers”',
    tags: '🍝 Pasta · 🥩 Ragù · “strotz-ah-PREH-tee”',
  },
  fr: {
    dishes: [
      { name: 'Pot-au-Feu', price: '14€' },
      { name: 'Croque Madame', price: '9€' },
      { name: 'Île Flottante', price: '7€' },
    ],
    highlight: 0,
    explain: 'Slow-simmered beef & vegetables — literally “pot on the fire”',
    tags: '🥩 Beef · 🍲 Comfort food · “po-toh-FEU”',
  },
  de: {
    dishes: [
      { name: 'Falscher Hase', price: '12€' },
      { name: 'Himmel und Erde', price: '10€' },
      { name: 'Maultaschen', price: '11€' },
    ],
    highlight: 0,
    explain: 'Baked meatloaf — literally “fake rabbit”',
    tags: '🥩 Beef & pork · 🥚 Contains egg',
  },
  jp: {
    dishes: [
      { name: 'Oyakodon · 親子丼', price: '¥900' },
      { name: 'Tsukune · つくね', price: '¥600' },
      { name: 'Karaage · 唐揚げ', price: '¥700' },
    ],
    highlight: 0,
    explain: 'Chicken & egg rice bowl — literally “parent-and-child bowl”',
    tags: '🍚 Rice bowl · 🥚 Egg · “oh-yah-koh-DON”',
  },
  mx: {
    dishes: [
      { name: 'Huevos Divorciados', price: '$95' },
      { name: 'Pozole Rojo', price: '$120' },
      { name: 'Tlayuda', price: '$110' },
    ],
    highlight: 0,
    explain: 'Two fried eggs split by red & green salsa — “divorced eggs”',
    tags: '🥚 Breakfast · 🌶 Two salsas',
  },
  tr: {
    dishes: [
      { name: 'İmam Bayıldı', price: '₺180' },
      { name: 'Hünkâr Beğendi', price: '₺240' },
      { name: 'Kuzu Tandır', price: '₺320' },
    ],
    highlight: 0,
    explain: 'Stuffed eggplant in olive oil — literally “the imam fainted”',
    tags: '🍆 Vegetarian · 🫒 Olive oil',
  },
  gr: {
    dishes: [
      { name: 'Papoutsakia', price: '12€' },
      { name: 'Saganaki', price: '8€' },
      { name: 'Kleftiko', price: '15€' },
    ],
    highlight: 0,
    explain: 'Stuffed eggplant halves — literally “little shoes”',
    tags: '🍆 Eggplant · 🧀 Béchamel',
  },
  cn: {
    dishes: [
      { name: '夫妻肺片 Fūqī Fèipiàn', price: '¥45' },
      { name: '蚂蚁上树 Mǎyǐ Shàng Shù', price: '¥28' },
      { name: '狮子头 Shīzi Tóu', price: '¥38' },
    ],
    highlight: 0,
    explain:
      'Sliced beef in chili oil — literally “husband & wife lung slices”',
    tags: '🌶 Very spicy · 🥩 Beef',
  },
  th: {
    dishes: [
      { name: 'Khao Soi · ข้าวซอย', price: '80฿' },
      { name: 'Som Tam · ส้มตำ', price: '60฿' },
      { name: 'Gaeng Massaman', price: '90฿' },
    ],
    highlight: 0,
    explain: 'Northern curry noodles with a crispy noodle crown',
    tags: '🌶 Medium · 🥥 Coconut · “cow-SOY”',
  },
  id: {
    dishes: [
      { name: 'Gado-Gado', price: '35k Rp' },
      { name: 'Nasi Goreng', price: '30k' },
      { name: 'Sate Ayam', price: '25k' },
    ],
    highlight: 0,
    explain: 'Vegetables in peanut sauce — literally “mix-mix”',
    tags: '🥜 Peanuts · 🌱 Vegetarian',
  },
};
// Countries sharing a menu
const COUNTRY_TO_MENU = {
  es: 'es',
  cu: 'es',
  it: 'it',
  fr: 'fr',
  de: 'de',
  at: 'de',
  jp: 'jp',
  mx: 'mx',
  tr: 'tr',
  gr: 'gr',
  cn: 'cn',
  tw: 'cn',
  th: 'th',
  id: 'id',
};

// "Travel tool" promo for Menu Translator (our own app), composed as a
// little restaurant-menu artifact so it reads as part of the story, not an
// ad. Uses the official store badges (required by Apple/Google brand
// policies); buttons adapt to the visitor's platform. Inserted late in
// long posts only; removed on mount for paying ('paid' role) viewers —
// free members and logged-out readers keep it (it promotes the app, not
// a blog signup).
export default function MenuTranslatorCard({ countryCode }) {
  const [hidden, setHidden] = useState(false);
  const [platform, setPlatform] = useState(null);
  const plausible = usePlausible();

  useEffect(() => {
    // Paying members never see promos — neither in their own posts nor
    // while reading others'. Free accounts and logged-out readers do.
    // (Posts BY paid authors never get this card — the parser skips
    // inline promos for them entirely.)
    let roles;
    try {
      roles = getRoles();
    } catch {
      roles = undefined;
    }
    if (roles && roles.indexOf('paid') !== -1) setHidden(true);
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) setPlatform('ios');
    else if (/Android/i.test(ua)) setPlatform('android');
    else setPlatform('desktop');
  }, []);

  if (hidden) return null;

  const menu =
    MENUS[COUNTRY_TO_MENU[(countryCode || '').toLowerCase()]] || MENUS.default;

  return (
    <aside
      className={`${figtree.className} tf-inline-promo tf-promo-menu tw-relative tw-my-12 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-width:1.5px] tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)]`}
    >
      <div className="tw-flex tw-flex-col tw-gap-6 tw-p-6 sm:tw-flex-row sm:tw-items-center sm:tw-gap-8 sm:tw-p-8">
        {/* The menu artifact */}
        <div
          aria-hidden="true"
          className="tw-mx-auto tw-w-full tw-max-w-[15rem] tw-shrink-0 -tw-rotate-1 tw-rounded-lg tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#3A4D44] tw-bg-[#FFFDF6] dark:tw-bg-[#0F1D18] tw-px-5 tw-py-4 tw-shadow-[0_14px_30px_-18px_rgba(29,45,40,0.5)]"
        >
          <p className="tw-m-0 tw-border-0 tw-border-b tw-border-double tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-bottom-width:3px] tw-pb-2 tw-text-center tw-font-mono tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-[0.4em] tw-text-[#8A958F]">
            Menu
          </p>
          <div
            className={`${readingSerif.className} tw-mt-3 tw-text-[13.5px] tw-italic tw-leading-relaxed tw-text-[#243630] dark:tw-text-[#CFDAD2]`}
          >
            {menu.dishes.map((dish, i) => (
              <div key={dish.name}>
                <p
                  className={`tw-m-0 tw-flex tw-items-baseline tw-gap-1${
                    i > 0 ? ' tw-mt-1' : ''
                  }`}
                >
                  <span
                    className={
                      i === menu.highlight
                        ? 'tw-rounded tw-bg-primary-600/15 tw-px-1 dark:tw-bg-primary-300/20'
                        : undefined
                    }
                  >
                    {dish.name}
                  </span>
                  <span className="tw-flex-1 tw-border-0 tw-border-b tw-border-dotted tw-border-[#C9C0AA]" />
                  <span className="tw-whitespace-nowrap">{dish.price}</span>
                </p>
                {i === menu.highlight && (
                  <div className="tw-my-2 tw-rounded-md tw-border tw-border-solid tw-border-primary-600/30 tw-bg-primary-600/[0.07] dark:tw-bg-primary-300/10 tw-px-2.5 tw-py-1.5 tw-not-italic">
                    <p
                      className={`${figtree.className} tw-m-0 tw-text-[11px] tw-font-semibold tw-leading-snug tw-text-primary-800 dark:tw-text-primary-200`}
                    >
                      {menu.explain}
                    </p>
                    <p
                      className={`${figtree.className} tw-m-0 tw-mt-0.5 tw-text-[10px] tw-leading-snug tw-text-[#5F6F69] dark:tw-text-[#93A69E]`}
                    >
                      {menu.tags}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Copy + official store badges */}
        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-m-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
            Travel tool · from the TravelFeed team
          </p>
          <p
            className={`${fraunces.className} tw-mb-0 tw-mt-2.5 tw-text-[1.45rem] tw-font-semibold tw-leading-snug tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]`}
          >
            Understand every dish, not just the words.
          </p>
          <p className="tw-mb-0 tw-mt-2.5 tw-max-w-md tw-text-[15px] tw-leading-relaxed tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
            Word-for-word translation gets menus hilariously wrong. Snap a
            photo (handwritten menus work too) and you&apos;ll know what each
            dish actually is, what&apos;s in it, and how to ask for it.
          </p>
          <div className="tw-mt-5 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            {platform !== 'android' && (
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  plausible('CTA: Menu Translator', {
                    props: { target: 'appstore' },
                  })
                }
                className="tw-transition-transform tw-duration-200 hover:-tw-translate-y-0.5"
              >
                <Image
                  src={appStoreBadge}
                  alt="Download on the App Store"
                  className="tw-h-[44px] tw-w-auto"
                  unoptimized
                />
              </a>
            )}
            {platform !== 'ios' && (
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  plausible('CTA: Menu Translator', {
                    props: { target: 'playstore' },
                  })
                }
                className="tw-transition-transform tw-duration-200 hover:-tw-translate-y-0.5"
              >
                <Image
                  src={playStoreBadge}
                  alt="Get it on Google Play"
                  className="tw-h-[44px] tw-w-auto"
                  unoptimized
                />
              </a>
            )}
            {(platform === 'desktop' || platform === null) && (
              <a
                href={WEBSITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  plausible('CTA: Menu Translator', {
                    props: { target: 'website' },
                  })
                }
                className="tf-text-link tw-font-mono tw-text-[11px] tw-font-semibold tw-tracking-wide"
              >
                menutranslatorapp.com →
              </a>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
